import { getFirestore, updateDoc, doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';

interface BaseItem {
  id: string;
  name_en: string;
  name_es: string;
  description_en: string;
  description_es: string;
}

interface Category extends BaseItem {}

interface Skill extends BaseItem {
  categoryId: string;
}

interface Certification extends BaseItem {
  issuer_en: string;
  issuer_es: string;
  duration_en: string;
  duration_es: string;
  categoryId: string;
}

const SkillsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [activeTab, setActiveTab] = useState<'categories' | 'skills' | 'certifications'>('categories');
  // Nuevo estado para el tab interno
  const [activeDomainTab, setActiveDomainTab] = useState<'construccion' | 'salud'>('construccion');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newItem, setNewItem] = useState<{
    name_en: string;
    name_es: string;
    description_en: string;
    description_es: string;
    categoryId?: string;
    issuer_en?: string;
    issuer_es?: string;
    duration_en?: string;
    duration_es?: string;
  }>({
    name_en: '',
    name_es: '',
    description_en: '',
    description_es: ''
  });
  const [editingItem, setEditingItem] = useState<Category | Skill | Certification | null>(null);
  const [toast, setToast] = useState<{message: string, type: 'success'|'error'}|null>(null);
  const db = getFirestore();

  // Helper para obtener el nombre del documento según el tab interno
  const getDocName = () =>
    activeDomainTab === 'construccion' ? 'skills_and_certs' : 'Health_skills_certs';

  useEffect(() => {
    const loadData = async () => {
      try {
        const docRef = doc(db, 'Skills_and_certifications', getDocName());
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('[SkillsManagement] Datos Firestore:', data);
          setCategories(Array.isArray(data.categories) ? data.categories : []);
          setSkills(Array.isArray(data.skills) ? data.skills : []);
          setCertifications(Array.isArray(data.certifications) ? data.certifications : []);
        } else {
          console.log('[SkillsManagement] No existe el documento');
          setCategories([]);
          setSkills([]);
          setCertifications([]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [db, activeDomainTab]);

  const handleDelete = async (itemId: string) => {
    try {
      const docRef = doc(db, 'Skills_and_certifications', getDocName());
      let updateData = {};
      
      switch (activeTab) {
        case 'categories':
          updateData = { categories: categories.filter(cat => cat.id !== itemId) };
          setCategories(categories.filter(cat => cat.id !== itemId));
          break;
        case 'skills':
          updateData = { skills: skills.filter(skill => skill.id !== itemId) };
          setSkills(skills.filter(skill => skill.id !== itemId));
          break;
        case 'certifications':
          updateData = { certifications: certifications.filter(cert => cert.id !== itemId) };
          setCertifications(certifications.filter(cert => cert.id !== itemId));
          break;
      }

      await updateDoc(docRef, updateData);
      setToast({ message: t('admin.skills.deleted'), type: 'success' });
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error deleting item:', error);
      setToast({ message: t('admin.skills.error'), type: 'error' });
      setTimeout(() => setToast(null), 2500);
    }
  };

  const handleEdit = (item: Category | Skill | Certification) => {
    setEditingItem(item);
    setNewItem({
      name_en: item.name_en,
      name_es: item.name_es,
      description_en: item.description_en,
      description_es: item.description_es,
      ...(item as Skill | Certification).categoryId && { categoryId: (item as Skill | Certification).categoryId },
      ...(item as Certification).issuer_en && { issuer_en: (item as Certification).issuer_en },
      ...(item as Certification).issuer_es && { issuer_es: (item as Certification).issuer_es },
      ...(item as Certification).duration_en && { duration_en: (item as Certification).duration_en },
      ...(item as Certification).duration_es && { duration_es: (item as Certification).duration_es }
    });
    setIsModalOpen(true);
  };

  const handleCreateItem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const docRef = doc(db, 'Skills_and_certifications', getDocName());
      const baseItem = {
        id: editingItem?.id || Date.now().toString(),
        name_en: newItem.name_en || '',
        name_es: newItem.name_es || '',
        description_en: newItem.description_en || '',
        description_es: newItem.description_es || ''
      };

      let updateData = {};
      switch (activeTab) {
        case 'categories':
          const newCategory: Category = baseItem;
          if (editingItem) {
            updateData = { 
              categories: categories.map(cat => 
                cat.id === editingItem.id ? newCategory : cat
              )
            };
            setCategories(categories.map(cat => 
              cat.id === editingItem.id ? newCategory : cat
            ));
          } else {
            updateData = { categories: [...categories, newCategory] };
            setCategories([...categories, newCategory]);
          }
          break;
        case 'skills':
          if (!newItem.categoryId) {
            throw new Error('Category ID is required for skills');
          }
          const newSkill: Skill = {
            ...baseItem,
            categoryId: newItem.categoryId
          };
          if (editingItem) {
            updateData = { 
              skills: skills.map(skill => 
                skill.id === editingItem.id ? newSkill : skill
              )
            };
            setSkills(skills.map(skill => 
              skill.id === editingItem.id ? newSkill : skill
            ));
          } else {
            updateData = { skills: [...skills, newSkill] };
            setSkills([...skills, newSkill]);
          }
          break;
        case 'certifications':
          if (!newItem.categoryId || !newItem.issuer_en || !newItem.issuer_es || !newItem.duration_en || !newItem.duration_es) {
            throw new Error('Missing required fields for certification');
          }
          const newCertification: Certification = {
            ...baseItem,
            categoryId: newItem.categoryId,
            issuer_en: newItem.issuer_en,
            issuer_es: newItem.issuer_es,
            duration_en: newItem.duration_en,
            duration_es: newItem.duration_es
          };
          if (editingItem) {
            updateData = { 
              certifications: certifications.map(cert => 
                cert.id === editingItem.id ? newCertification : cert
              )
            };
            setCertifications(certifications.map(cert => 
              cert.id === editingItem.id ? newCertification : cert
            ));
          } else {
            updateData = { certifications: [...certifications, newCertification] };
            setCertifications([...certifications, newCertification]);
          }
          break;
      }

      await updateDoc(docRef, updateData);
      setIsModalOpen(false);
      setEditingItem(null);
      setNewItem({
        name_en: '',
        name_es: '',
        description_en: '',
        description_es: ''
      });
      setToast({ message: t('admin.skills.success'), type: 'success' });
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error creating/updating item:', error);
      setToast({ message: t('admin.skills.error'), type: 'error' });
      setTimeout(() => setToast(null), 2500);
    }
  };

  // Tabs internos para Construcción y Salud como badges
  const renderDomainBadges = () => (
    <span className="ml-2 inline-flex gap-2 align-middle">
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-200 ${
          activeDomainTab === 'construccion'
            ? 'bg-red-500 text-white border-red-500'
            : 'bg-gray-200 text-gray-700 border-gray-200'
        }`}
        style={{ cursor: 'pointer' }}
        onClick={e => { e.stopPropagation(); setActiveDomainTab('construccion'); }}
      >
        {t('admin.skills.construction')}
      </span>
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-200 ${
          activeDomainTab === 'salud'
            ? 'bg-red-500 text-white border-red-500'
            : 'bg-gray-200 text-gray-700 border-gray-200'
        }`}
        style={{ cursor: 'pointer' }}
        onClick={e => { e.stopPropagation(); setActiveDomainTab('salud'); }}
      >
        {t('admin.skills.health')}
      </span>
    </span>
  );

  const renderContent = () => {
    const items = activeTab === 'categories' ? categories : 
                 activeTab === 'skills' ? skills : 
                 certifications;
    // Asegura que items sea siempre un array
    const safeItems = Array.isArray(items) ? items : [];
    return (
      <>
        <button 
          onClick={() => {
            setEditingItem(null);
            setNewItem({
              name_en: '',
              name_es: '',
              description_en: '',
              description_es: ''
            });
            setIsModalOpen(true);
          }}
          className="bg-red-600 hover:bg-red-700 text-white border-none py-3 px-6 rounded-lg cursor-pointer text-base font-medium transition-all duration-200 ease-in-out flex items-center gap-2 mb-6"
        >
          {t(`admin.skills.add${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`)}
        </button>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {safeItems.map(item => (
            <div 
              key={item.id} 
              className="bg-gray-50 rounded-lg p-6 transition-all duration-200 ease-in-out border border-gray-200 relative hover:transform hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="absolute top-4 right-4 flex gap-2">
                <button 
                  onClick={() => handleEdit(item)}
                  className="bg-transparent border-none text-gray-600 p-2 cursor-pointer rounded flex items-center justify-center transition-all duration-200 ease-in-out hover:bg-gray-100 hover:text-red-600"
                >
                  <FaEdit />
                </button>
                <button 
                  onClick={() => handleDelete(item.id)}
                  className="bg-transparent border-none text-gray-600 p-2 cursor-pointer rounded flex items-center justify-center transition-all duration-200 ease-in-out hover:bg-gray-100 hover:text-red-600"
                >
                  <FaTrash />
                </button>
              </div>
              <h3 className="text-red-600 m-0 mb-3 text-xl font-semibold pr-16">{item.name_en}</h3>
              <p className="text-gray-600 text-sm leading-relaxed m-0">{item.description_en}</p>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="p-8 w-full min-h-screen bg-gray-100">
      <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-white to-red-100 py-12 px-4 flex flex-col items-center justify-start">
        <div className="max-w-7xl w-full mx-auto">
          {/* Header */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/40 p-10 mb-10">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight drop-shadow-lg">
              <span className="bg-gradient-to-r from-red-500 via-pink-400 to-red-600 bg-clip-text text-transparent">
                {t('admin.skills.title')}
              </span>
            </h1>
          </div>

          {/* Toast de alerta */}
          {toast && (
            <div className={`fixed top-6 right-6 z-[9999] px-6 py-4 rounded-xl shadow-lg text-white text-lg font-semibold transition-all duration-500 animate-fadeIn ${toast.type === 'success' ? 'bg-red-600' : 'bg-black'}`}>
              {toast.message}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b-2 border-gray-200 pb-4">
            <button 
              onClick={() => setActiveTab('categories')}
              className={`py-3 px-6 border-none rounded-lg cursor-pointer text-base font-medium transition-all duration-200 ease-in-out ${
                activeTab === 'categories' 
                  ? 'bg-red-600 text-white font-semibold' 
                  : 'bg-transparent text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t('admin.skills.categories')}
              {activeTab === 'categories' && renderDomainBadges()}
            </button>
            <button 
              onClick={() => setActiveTab('skills')}
              className={`py-3 px-6 border-none rounded-lg cursor-pointer text-base font-medium transition-all duration-200 ease-in-out ${
                activeTab === 'skills' 
                  ? 'bg-red-600 text-white font-semibold' 
                  : 'bg-transparent text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t('admin.skills.skills')}
              {activeTab === 'skills' && renderDomainBadges()}
            </button>
            <button 
              onClick={() => setActiveTab('certifications')}
              className={`py-3 px-6 border-none rounded-lg cursor-pointer text-base font-medium transition-all duration-200 ease-in-out ${
                activeTab === 'certifications' 
                  ? 'bg-red-600 text-white font-semibold' 
                  : 'bg-transparent text-gray-900 hover:bg-gray-100'
              }`}
            >
              {t('admin.skills.certifications')}
              {activeTab === 'certifications' && renderDomainBadges()}
            </button>
          </div>

          {/* Content Section */}
          <div className="bg-white rounded-3xl p-10 shadow-2xl border border-white/40">
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl p-10 w-full max-w-2xl shadow-2xl border border-white/40 animate-fadeInUp overflow-y-auto max-h-[90vh]">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-8 tracking-tight drop-shadow-lg">
              {t(`admin.skills.add${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`)}
            </h2>
            <form onSubmit={handleCreateItem} className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-gray-900 font-bold">{t('admin.skills.nameEn')}</label>
                <input
                  type="text"
                  value={newItem.name_en}
                  onChange={(e) => setNewItem({...newItem, name_en: e.target.value})}
                  required
                  className="py-3 px-3 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-gray-900 font-bold">{t('admin.skills.nameEs')}</label>
                <input
                  type="text"
                  value={newItem.name_es}
                  onChange={(e) => setNewItem({...newItem, name_es: e.target.value})}
                  required
                  className="py-3 px-3 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-gray-900 font-bold">{t('admin.skills.descriptionEn')}</label>
                <textarea
                  value={newItem.description_en}
                  onChange={(e) => setNewItem({...newItem, description_en: e.target.value})}
                  required
                  className="py-3 px-3 border-2 border-gray-300 rounded-xl text-base min-h-24 resize-y focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-gray-900 font-bold">{t('admin.skills.descriptionEs')}</label>
                <textarea
                  value={newItem.description_es}
                  onChange={(e) => setNewItem({...newItem, description_es: e.target.value})}
                  required
                  className="py-3 px-3 border-2 border-gray-300 rounded-xl text-base min-h-24 resize-y focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                />
              </div>
              
              {/* Campos adicionales para Skills y Certifications */}
              {(activeTab === 'skills' || activeTab === 'certifications') && (
                <div className="flex flex-col gap-2">
                  <label className="text-gray-900 font-bold">{t('admin.skills.category')}</label>
                  <select
                    value={newItem.categoryId || ''}
                    onChange={(e) => setNewItem({...newItem, categoryId: e.target.value})}
                    required
                    className="py-3 px-3 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                  >
                    <option value="">{t('admin.skills.selectCategory')}</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name_en}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {activeTab === 'certifications' && (
                <>
                  <div className="flex flex-col gap-2">
                    <label className="text-gray-900 font-bold">{t('admin.skills.issuerEn')}</label>
                    <input
                      type="text"
                      value={newItem.issuer_en || ''}
                      onChange={(e) => setNewItem({...newItem, issuer_en: e.target.value})}
                      required
                      className="py-3 px-3 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-gray-900 font-bold">{t('admin.skills.issuerEs')}</label>
                    <input
                      type="text"
                      value={newItem.issuer_es || ''}
                      onChange={(e) => setNewItem({...newItem, issuer_es: e.target.value})}
                      required
                      className="py-3 px-3 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-gray-900 font-bold">{t('admin.skills.durationEn')}</label>
                    <input
                      type="text"
                      value={newItem.duration_en || ''}
                      onChange={(e) => setNewItem({...newItem, duration_en: e.target.value})}
                      required
                      className="py-3 px-3 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-gray-900 font-bold">{t('admin.skills.durationEs')}</label>
                    <input
                      type="text"
                      value={newItem.duration_es || ''}
                      onChange={(e) => setNewItem({...newItem, duration_es: e.target.value})}
                      required
                      className="py-3 px-3 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    />
                  </div>
                </>
              )}

              <div className="flex gap-4 mt-4">
                <button 
                  type="submit"
                  className="bg-red-600 hover:bg-red-700 text-white border-none py-3 px-6 rounded-lg cursor-pointer text-base font-medium transition-all duration-200 ease-in-out flex items-center gap-2"
                >
                  {t('common.save')}
                </button>
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-900 border-none py-3 px-6 rounded-lg cursor-pointer text-base font-medium transition-all duration-200 ease-in-out"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsManagement; 